import { Alert, AlertIcon, Button, Flex, HStack, Tag, Text, useToast } from "@chakra-ui/react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  type SavedReportDto,
  SavedReportKindDto,
  type SavedReportListDto,
  useListSavedReportsQuery,
  useReplaceSavedReportQueryMutation,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../auth/useLoggedInWithOrgContextState";
import { personName } from "../components/communication/communicationUtils";
import { useApiError } from "../errors/useApiError";
import { savedReportQuery } from "./expiringThisYearReport";
import { useRemountList } from "./RemountOnReportOpen";
import { ReportsMenu } from "./ReportsMenu";
import { reportSaveBlockedReason } from "./reportSaveBlockedReason";
import { SaveAsReportButton } from "./SaveAsReportButton";
import { Tooltip } from "../components/Tooltip";
import {
  isSameReportQuery,
  OPEN_REPORT_PARAM,
  type ReportQueryComparison,
  savedReportLink,
  savedReportListPath,
  savedReportListAllLabel,
  storedReportQuery,
} from "./savedReportLists";

interface Args {
  list: SavedReportListDto;
  query: string;
  heading: ReactNode;
  listFailedToLoad: boolean;
  listIsLoading: boolean;
  comparable?: ReportQueryComparison;
}

export const useListReports = ({ list, query, heading, listFailedToLoad, listIsLoading, comparable }: Args) => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const [searchParams] = useSearchParams();
  const { pathname } = useLocation();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const {
    data,
    isLoading: reportsLoading,
    isError: reportsFailed,
    isSuccess: reportsLoaded,
    refetch,
  } = useListSavedReportsQuery({ organizationId: selectedOrg.id });
  const [replaceQuery, { isLoading: isSaving }] = useReplaceSavedReportQueryMutation();
  const [isNaming, setIsNaming] = useState(false);
  const remountList = useRemountList();
  const [savedHere, setSavedHere] = useState<SavedReportDto | null>(null);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [opensBareLink] = useState(
    () => searchParams.has(OPEN_REPORT_PARAM) && [...searchParams.keys()].every((key) => key === OPEN_REPORT_PARAM)
  );

  const reports = (data?.reports ?? []).filter((report) => report.list === list);
  const openReportId = searchParams.get(OPEN_REPORT_PARAM);
  const openReport =
    reports.find((report) => report.id === openReportId) ?? (savedHere?.id === openReportId ? savedHere : null);
  const currentQuery = storedReportQuery(query);
  const openReportMissing = openReportId !== null && reportsLoaded && openReport === null;
  const openReportFailed = openReportId !== null && reportsFailed;
  const saveChangesBlockedReason = reportSaveBlockedReason(t, currentQuery, listFailedToLoad);
  const waitsForReportView = opensBareLink && openReport !== null && storedReportQuery(openReport.query) !== "";
  const changed =
    openReport !== null && !waitsForReportView && !isSameReportQuery(savedReportQuery(openReport), currentQuery, comparable);

  const reportViewLink = waitsForReportView ? savedReportLink(openReport) : null;
  useEffect(() => {
    if (reportViewLink === null) return;
    navigate(reportViewLink, { replace: true });
    remountList();
  }, [reportViewLink, navigate, remountList]);

  const openPath = (path: string) => {
    navigate(path);
    remountList();
  };
  const open = (report: SavedReportDto) => openPath(savedReportLink(report));

  const discardChanges = async (report: SavedReportDto) => {
    if (isDiscarding) return;
    setIsDiscarding(true);
    try {
      const fresh = await refetch().unwrap();
      const saved = fresh.reports.find((candidate) => candidate.id === report.id);
      if (saved) open(saved);
      else if (savedHere?.id === report.id) setSavedHere(null);
    } catch {
      open(report);
    } finally {
      setIsDiscarding(false);
    }
  };

  const showSavedReport = (report: SavedReportDto) => {
    setSavedHere(report);
    const params = new URLSearchParams(searchParams);
    params.set(OPEN_REPORT_PARAM, report.id);
    navigate(`${pathname}?${params.toString()}`, { replace: true });
  };

  const saveChanges = async () => {
    if (!openReport) return;
    try {
      await replaceQuery({
        savedReportId: openReport.id,
        replaceSavedReportQueryRequest: { query: currentQuery },
      }).unwrap();
      toast({ title: t("The changes are saved in the report"), status: "success" });
    } catch (e) {
      trigger(e);
    }
  };

  const reportHeading = openReport ? (
    <HStack as="span" spacing="2" flexWrap="wrap" align="center">
      <Text as="span">{openReport.name}</Text>
      <Tag size="sm" colorScheme="teal" variant="subtle">
        {
          {
            [SavedReportKindDto.Firm]: t("Firm report"),
            [SavedReportKindDto.Own]: t("My report"),
            [SavedReportKindDto.Shared]: t("Shared by {{name}}", {
              name: openReport.sharedBy ? personName(openReport.sharedBy) : "",
            }),
          }[openReport.kind]
        }
      </Tag>
      {changed && (
        <>
          <Tag size="sm" colorScheme="orange" variant="subtle">
            {t("Changed")}
          </Tag>
          <Button
            size="xs"
            variant="link"
            colorScheme="teal"
            onClick={() => void discardChanges(openReport)}
            isLoading={isDiscarding}
          >
            {t("Discard changes")}
          </Button>
          {openReport.canChange && (
            <Tooltip label={saveChangesBlockedReason} shouldWrapChildren>
              <Button
                size="xs"
                colorScheme="teal"
                onClick={() => void saveChanges()}
                isLoading={isSaving}
                isDisabled={!!saveChangesBlockedReason || listIsLoading}
              >
                {t("Save changes")}
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </HStack>
  ) : (
    heading
  );

  const actions = (
    <>
      <ReportsMenu
        list={list}
        reports={reports}
        isLoading={reportsLoading}
        failedToLoad={reportsFailed}
        onRetry={() => void refetch()}
        openReportId={openReport?.id ?? null}
        onOpen={open}
        onOpenAll={() => openPath(savedReportListPath(list))}
        onSaveCurrentView={() => setIsNaming(true)}
      />
      <SaveAsReportButton
        list={list}
        query={currentQuery}
        listFailedToLoad={listFailedToLoad}
        listIsLoading={listIsLoading}
        canSaveFirmReports={data?.canSaveFirmReports ?? false}
        reportsLoading={reportsLoading}
        reportsFailed={reportsFailed}
        onRetryReports={() => void refetch()}
        isNaming={isNaming}
        onNamingChange={setIsNaming}
        onSaved={showSavedReport}
      />
    </>
  );

  const notice = openReportFailed ? (
    <Alert status="error" borderRadius="md" fontSize="sm" my="3">
      <AlertIcon />
      <Flex gap="3" align="center" wrap="wrap">
        <Text>{t("The report could not be loaded.")}</Text>
        <Button size="sm" variant="link" colorScheme="teal" onClick={() => void refetch()}>
          {t("Try again")}
        </Button>
      </Flex>
    </Alert>
  ) : (
    openReportMissing && (
      <Alert status="warning" borderRadius="md" fontSize="sm" my="3">
        <AlertIcon />
        <Flex gap="3" align="center" wrap="wrap">
          <Text>{t("The report no longer exists, or is no longer shared with you.")}</Text>
          <Button size="sm" variant="link" colorScheme="teal" onClick={() => openPath(savedReportListPath(list))}>
            {t("Show {{list}}", { list: savedReportListAllLabel(t, list).toLowerCase() })}
          </Button>
        </Flex>
      </Alert>
    )
  );

  return { heading: reportHeading, actions, notice };
};
